import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {
  Card,
  TextStyle,
  Heading,
  TextContainer,
  Stack,
  Caption,
  Button,
} from '@shopify/polaris';
import {Money} from 'sections/types';
import {formatCurrency} from 'utilities/numbers';
import {Routes} from '../../utilities';
import {LoginModal, LoginModalContext} from '../login-modal';
import withAuth from 'sections/services-marketplace/experts/sections/with-auth/withAuth';
import {GetShopAuthQueryData} from 'sections/services-marketplace/experts/sections/with-auth/graphql/GetShopAuth.graphql';

import styles from './RequestQuote.scss';
import DirectContactModal from './DirectContactModal';

interface OwnProps {
  startingPrice?: Money;
  serviceHandle: string;
  acceptingLeads: boolean;
  expertHandle: string;
  businessName: string;
  currentShop: GetShopAuthQueryData.CurrentShop | null;
  currentShopUser: GetShopAuthQueryData.CurrentShopUser | null;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  loginModalOpen: boolean;
  directContactModalOpen: boolean;
}

class RequestQuote extends React.Component<CombinedProps, State> {
  state: State = {
    loginModalOpen: false,
    directContactModalOpen: false,
  };

  toggleLoginModal = () => {
    this.setState(({loginModalOpen}) => ({loginModalOpen: !loginModalOpen}));
  };

  toggleDirectContactModal = () => {
    this.setState(({directContactModalOpen}) => ({
      directContactModalOpen: !directContactModalOpen,
    }));
  };

  swapActiveModal = () => {
    this.toggleLoginModal();
    this.toggleDirectContactModal();
  };

  renderAuthenticatedCallout = (fullWidth = false) => {
    const {
      acceptingLeads,
      currentShop,
      serviceHandle,
      expertHandle,
      i18n,
    } = this.props;

    return currentShop ? (
      <Button
        primary
        url={Routes.postJobLoginPath({
          shop: currentShop.permanentDomain,
          serviceHandle,
          expertHandle,
        })}
        external
        fullWidth={fullWidth}
        disabled={!acceptingLeads}
      >
        {i18n.translate('Profile.RequestQuote.buttonText')}
      </Button>
    ) : null;
  };

  renderUnauthenticatedCallout = (fullWidth = false) => {
    const {acceptingLeads, i18n} = this.props;

    return (
      <Button
        primary
        onClick={this.toggleLoginModal}
        disabled={!acceptingLeads}
        fullWidth={fullWidth}
      >
        {i18n.translate('Profile.RequestQuote.buttonText')}
      </Button>
    );
  };

  renderHeader = () => {
    const {startingPrice, i18n} = this.props;

    return (
      <>
        <TextStyle variation="subdued">
          {i18n.translate('Profile.RequestQuote.priceTitle')}
        </TextStyle>
        <Heading element="p">
          {startingPrice && Number(startingPrice.amount)
            ? formatCurrency(
                startingPrice.amount,
                0,
                startingPrice.currencyCode
              )
            : i18n.translate('Profile.ProfileMetrics.noPrice')}
        </Heading>
      </>
    );
  };

  renderCaption = () => {
    const {acceptingLeads, i18n} = this.props;

    return (
      <TextStyle variation="subdued">
        <Caption>
          {acceptingLeads
            ? i18n.translate('Profile.RequestQuote.notice')
            : i18n.translate('Profile.RequestQuote.noticeInactive')}
        </Caption>
      </TextStyle>
    );
  };

  render() {
    const {
      currentShop,
      serviceHandle,
      expertHandle,
      businessName,
      i18n,
    } = this.props;

    const {loginModalOpen, directContactModalOpen} = this.state;

    const captionLink = (
      <>
        {i18n.translate('Profile.RequestQuote.captionLink', {
          captionLinkText: (
            <Button onClick={this.swapActiveModal} plain>
              {i18n.translate('Profile.RequestQuote.captionLinkText')}
            </Button>
          ),
        })}
      </>
    );

    return (
      <>
        <div className={styles.RequestQuote}>
          <Card>
            <Card.Section>
              {this.renderHeader()}
              <div className={styles.Container}>
                <TextContainer>
                  {currentShop
                    ? this.renderAuthenticatedCallout(true)
                    : this.renderUnauthenticatedCallout(true)}
                  <Stack distribution="center" spacing="none">
                    {this.renderCaption()}
                  </Stack>
                </TextContainer>
              </div>
            </Card.Section>
          </Card>
        </div>
        <div className={styles.RequestQuoteFooter}>
          <Stack alignment="center">
            <Stack.Item fill>{this.renderHeader()}</Stack.Item>
            {currentShop
              ? this.renderAuthenticatedCallout()
              : this.renderUnauthenticatedCallout()}
          </Stack>
        </div>
        {loginModalOpen ? (
          <LoginModal
            open={loginModalOpen}
            context={LoginModalContext.RequestQuote}
            onClose={this.toggleLoginModal}
            expertHandle={expertHandle}
            serviceHandle={serviceHandle}
            captionLink={captionLink}
          />
        ) : (
          <DirectContactModal
            open={directContactModalOpen}
            onClose={this.toggleDirectContactModal}
            expertHandle={expertHandle}
            serviceHandle={serviceHandle}
            businessName={businessName}
          />
        )}
      </>
    );
  }
}

export default withAuth(withI18n()(RequestQuote));
